export type RouteLayout = 'auth' | 'shell';

export interface RouteMeta {
    pageTitle?: string;
    roles?: string[];
    bodyClasses?: string;
    public?: boolean;
    /** Only pure (global) advisors may access the route. */
    pureAdvisor?: boolean;
}

export interface RouteDefinition {
    name: string;
    path: string;
    layout: RouteLayout;
    meta: RouteMeta;
}

/** Absolute paths keyed by legacy ui-router state name. */
export const ROUTE_MANIFEST: RouteDefinition[] = [
    { name: 'onboarding', path: '/onboarding', layout: 'auth', meta: { pageTitle: 'WELCOME', public: true } },
    { name: 'onboarding/method', path: '/onboarding/method', layout: 'auth', meta: { pageTitle: 'SELECT_LOGIN_METHOD', public: true } },
    { name: 'onboarding/password', path: '/onboarding/password', layout: 'auth', meta: { pageTitle: 'LOGIN_PASSWORD', public: true } },
    { name: 'onboarding/sent', path: '/onboarding/sent', layout: 'auth', meta: { pageTitle: 'LOGIN_SENT', public: true } },
    { name: 'onboarding/employeesent', path: '/onboarding/employeesent', layout: 'auth', meta: { pageTitle: 'EMPLOYEE_CODE_SENT', public: true } },
    { name: 'forgot', path: '/forgot', layout: 'auth', meta: { pageTitle: 'FORGOT_PASSWORD', public: true } },
    { name: 'require', path: '/require', layout: 'auth', meta: { pageTitle: 'REQUIRE_TEST', public: true } },
    { name: 'access', path: '/access/*', layout: 'auth', meta: { pageTitle: 'Accessing', public: true } },
    { name: 'callback', path: '/callback', layout: 'auth', meta: { public: true } },

    { name: 'private.dashboard', path: '/private', layout: 'shell', meta: { pageTitle: 'SIDEMENU_DASHBOARD', roles: ['MANAGER', 'ADMIN', 'ADVISOR'] } },
    { name: 'overview.dashboard', path: '/overview', layout: 'shell', meta: { pageTitle: 'OVERVIEW_TITLE', roles: ['MANAGER', 'ADMIN', 'ADVISOR'] } },
    { name: 'actions.list', path: '/actions', layout: 'shell', meta: { pageTitle: 'SIDEMENU_ACTIONS' } },
    { name: 'announcements.list', path: '/announcements/list', layout: 'shell', meta: { pageTitle: 'SIDEMENU_ANNOUNCEMENTS', roles: ['MANAGER', 'ADMIN', 'ADVISOR'] } },
    { name: 'rewards.home', path: '/rewards/:tab?', layout: 'shell', meta: { pageTitle: 'SIDEMENU_REWARDS', roles: ['ADMIN', 'ADVISOR'] } },
    { name: 'engagement.screen', path: '/engagement', layout: 'shell', meta: { pageTitle: 'SIDEMENU_ENGAGEMENT', roles: ['MANAGER', 'ADMIN', 'ADVISOR'] } },
    { name: 'topics.browse', path: '/topics', layout: 'shell', meta: { pageTitle: 'SIDEMENU_TOPICS_QUESTIONS', roles: ['MANAGER', 'ADMIN', 'ADVISOR'] } },

    { name: 'feedback.detail', path: '/feedback', layout: 'shell', meta: { pageTitle: 'SIDEMENU_FEEDBACK', roles: ['MANAGER', 'ADMIN', 'ADVISOR'] } },
    { name: 'feedback.comments', path: '/feedback/comments', layout: 'shell', meta: { pageTitle: 'COMMENTS_DETAIL', roles: ['MANAGER', 'ADMIN', 'ADVISOR'] } },
    { name: 'feedback.conversations', path: '/feedback/conversations', layout: 'shell', meta: { pageTitle: 'CONVERSATIONS_SECTION_TITLE', roles: ['ADMIN', 'MANAGER', 'ADVISOR'] } },
    { name: 'feedback.ideas', path: '/feedback/ideas', layout: 'shell', meta: { pageTitle: 'SIDEMENU_IDEAS', roles: ['MANAGER', 'ADMIN', 'ADVISOR'] } },
    { name: 'feedback.initiativeDetail', path: '/feedback/ideas/initiative/detail/:initiativeId', layout: 'shell', meta: { pageTitle: 'SIDEMENU_IDEAS', roles: ['MANAGER', 'ADMIN', 'ADVISOR'] } },
    { name: 'feedback.recognition', path: '/feedback/recognition', layout: 'shell', meta: { pageTitle: 'COMMENTS_DETAIL', roles: ['MANAGER', 'ADMIN', 'ADVISOR'] } },

    { name: 'surveys.list', path: '/surveys', layout: 'shell', meta: { pageTitle: 'SIDEMENU_SURVEYS', roles: ['MANAGER', 'ADMIN', 'ADVISOR'] } },
    { name: 'surveys.selector', path: '/surveys/selector', layout: 'shell', meta: { pageTitle: 'SIDEMENU_ENGAGEMENT', roles: ['MANAGER', 'ADMIN', 'ADVISOR'] } },
    { name: 'surveys.edit', path: '/surveys/edit/:surveyId?/:surveyClone?', layout: 'shell', meta: { pageTitle: 'Surveys Edit', roles: ['ADMIN', 'ADVISOR'] } },
    { name: 'surveys.results', path: '/surveys/results/:surveyId', layout: 'shell', meta: { pageTitle: 'Surveys Results', roles: ['MANAGER', 'ADMIN', 'ADVISOR'] } },

    { name: 'manage.users', path: '/manage/users/manage', layout: 'shell', meta: { pageTitle: 'MANAGE_USERS', roles: ['ADMIN', 'ADVISOR'] } },
    { name: 'manage.users_bulk', path: '/manage/users/bulk', layout: 'shell', meta: { pageTitle: 'Bulk Manage Users', roles: ['ADMIN', 'ADVISOR'] } },
    { name: 'manage.shift', path: '/manage/users/shift', layout: 'shell', meta: { pageTitle: 'SHIFT_TITLE', roles: ['ADMIN', 'ADVISOR'] } },
    { name: 'focus_roles', path: '/focus-roles', layout: 'shell', meta: { pageTitle: 'FOCUS_ROLES_TITLE', roles: ['ADMIN', 'ADVISOR'] } },
    { name: 'manage.timetracking', path: '/manage/users/timetracking', layout: 'shell', meta: { pageTitle: 'TIME_TRACKING_TITLE', roles: ['ADMIN', 'ADVISOR'] } },

    { name: 'company.config', path: '/company/config/:section?', layout: 'shell', meta: { pageTitle: 'SIDEMENU_CONFIG', roles: ['ADMIN', 'ADVISOR'] } },

    { name: 'reports.activation', path: '/reports/activation/:hierarchyId?', layout: 'shell', meta: { pageTitle: 'SIDEMENU_PACE', roles: ['MANAGER', 'ADMIN', 'ADVISOR'] } },
    { name: 'reports.participation', path: '/reports/participation/:hierarchyId?', layout: 'shell', meta: { pageTitle: 'SIDEMENU_ANALYTICS_PARTICIPATION', roles: ['MANAGER', 'ADMIN', 'ADVISOR'] } },
    { name: 'reports.ranking', path: '/reports/ranking', layout: 'shell', meta: {} },

    { name: 'custom.lc', path: '/lc', layout: 'shell', meta: { pageTitle: 'Life Cycle', roles: ['MANAGER', 'ADMIN', 'ADVISOR'] } },
    { name: 'journey.screen', path: '/journey', layout: 'shell', meta: { pageTitle: 'SIDEMENU_JOURNEY', roles: ['MANAGER', 'ADMIN', 'ADVISOR'] } },

    { name: 'advisors.templates', path: '/advisors/advisors/advisors_template', layout: 'shell', meta: { pageTitle: 'Templates', roles: ['ADVISOR'] } },
    { name: 'advisors.company_create', path: '/advisors/advisors/company_create', layout: 'shell', meta: { pageTitle: 'Create Company', roles: ['ADVISOR'] } },
    { name: 'advisors.surveys', path: '/advisors/surveys', layout: 'shell', meta: { pageTitle: 'Surveys Catalog', roles: ['ADVISOR'] } },
    { name: 'advisors.survey-edit', path: '/advisors/survey/edit/:templateId', layout: 'shell', meta: { pageTitle: 'Surveys Edit', roles: ['ADVISOR'] } },
    { name: 'advisors.management', path: '/advisors/advisors/advisors_management', layout: 'shell', meta: { pageTitle: 'SIDEMENU_ADVISORS_MANAGEMENT', roles: ['ADVISOR'], pureAdvisor: true } },
    { name: 'advisors.employee_locator', path: '/advisors/advisors/employee_locator', layout: 'shell', meta: { pageTitle: 'Employee Locator', roles: ['ADVISOR'] } },
    { name: 'advisors.absences', path: '/advisors/absences', layout: 'shell', meta: { pageTitle: 'Absences', roles: ['ADVISOR'] } },

    { name: 'design.system', path: '/design-system', layout: 'shell', meta: { pageTitle: 'Design System', roles: ['ADVISOR'] } }
];

const STATE_TO_ROUTE = new Map(ROUTE_MANIFEST.map((route) => [route.name, route]));

export const getRouteByName = (name: string): RouteDefinition | undefined => STATE_TO_ROUTE.get(name);

const pathPatternToRegex = (pattern: string): RegExp => {
    const escaped = pattern
        .replace(/\*/g, '.*')
        .replace(/\/:([^/?]+)\?/g, '(?:/[^/]+)?')
        .replace(/\/:([^/]+)/g, '/[^/]+')
        .replace(/\//g, '\\/');
    return new RegExp(`^${escaped}$`);
};

export const routeMatchesPath = (routePath: string, pathname: string): boolean => {
    const normalized = pathname.replace(/\/$/, '') || '/';
    return pathPatternToRegex(routePath).test(normalized);
};

export const getRouteByPath = (pathname: string): RouteDefinition | undefined => {
    const normalized = pathname.replace(/\/$/, '') || '/';
    return ROUTE_MANIFEST.find((route) => pathPatternToRegex(route.path).test(normalized));
};

const appendQuery = (path: string, query?: Record<string, unknown>): string => {
    if (!query) {
        return path;
    }
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            params.set(key, String(value));
        }
    });
    const qs = params.toString();
    return qs ? `${path}?${qs}` : path;
};

/** Build an absolute path from a legacy state name and optional params. */
export const buildPathFromState = (
    stateName: string,
    params?: Record<string, unknown>,
    query?: Record<string, unknown>
): string => {
    const route = getRouteByName(stateName);
    if (!route) {
        console.warn(`[routing] Unknown state name: ${stateName}`);
        return '/onboarding';
    }

    const paramValues = { ...(params ?? {}) };

    if (route.path.includes('*') && paramValues.activationToken) {
        const token = String(paramValues.activationToken);
        delete paramValues.activationToken;
        return appendQuery(`/access/${token}`, query ?? paramValues);
    }

    let path = route.path;
    path = path.replace(/\/:([^/?]+)\?/g, (_match, key: string) => {
        const value = paramValues[key];
        delete paramValues[key];
        if (value === undefined || value === null || value === '') {
            return '';
        }
        return `/${encodeURIComponent(String(value))}`;
    });
    path = path.replace(/:([^/]+)/g, (_match, key: string) => {
        const value = paramValues[key];
        delete paramValues[key];
        if (value === undefined || value === null || value === '') {
            return '';
        }
        return encodeURIComponent(String(value));
    });

    path = path.replace(/\/+/g, '/').replace(/\/$/, '') || '/';
    return appendQuery(path, query ?? paramValues);
};

export const isPublicPath = (pathOrState: string): boolean => {
    if (pathOrState.startsWith('/')) {
        if (getRouteByPath(pathOrState)?.meta.public) {
            return true;
        }
    } else if (getRouteByName(pathOrState)?.meta.public) {
        return true;
    }
    const publicPatterns = ['access', 'forgot', 'onboarding', 'register', 'require', 'employeesent', 'callback'];
    return new RegExp(publicPatterns.join('|')).test(pathOrState);
};
